import _ from "lodash";
import moment from "moment";
import React from "react";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import useAllPlayers from "../../hooks/queryResults/useAllPlayers";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useTeams from "../../hooks/queryResults/useTeams";
import useWeek from "../../hooks/queryResults/useWeek";
import useAdminMode from "../../hooks/useAdminMode";
import useUrls from "../../hooks/useUrls";
import http from "../../services/httpService";
import Util from "../../util/util";
import PlayerImageByPos from "../player/PlayerImageByPos";
import { getPlayerName } from "../player/PlayerName";
import TeamLogo from "../team/TeamLogo";
import Loading from "../ui/Loading";

const StyledTable = styled.table`
  & tr.undone,
  & tr.undone:hover td {
    background-color: #ddd;
    color: #999;
  }
`;

export default function TransactionsTable({ transactions, transactionsUrl }) {
  const { league: leagueUrl, season: seasonUrl } = useParams();
  const { playersData, playersQuery } = useAllPlayers();
  const { teams, teamsQuery } = useTeams();
  const adminMode = useAdminMode();
  const { apiUrl } = useUrls();
  const queryClient = useQueryClient();
  const { currentWeek } = useWeek();
  const weekUrl = Util.weekToUrl(currentWeek);

  const undoTransactionMutation = useMutation(
    (transactionId) => {
      console.log(transactionId);

      const url = `${apiUrl}/leagues/transaction/${transactionId}/undo`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with no data`);
      return http.patch(url, null, config);
    },
    {
      onMutate: async (transactionId) => {
        await queryClient.cancelQueries(transactionsUrl);
        const previousTransactions = queryClient.getQueryData(transactionsUrl);
        queryClient.setQueryData(transactionsUrl, (old) => {
          const newData = _.cloneDeep(old);
          newData.transactions = newData.transactions.filter(
            (transaction) => transaction.id !== transactionId
          );

          return newData;
        });
        return { previousTransactions };
      },
      onSuccess: () => {
        console.log("undo transaction mutation - success");
      },
      onError: (error, __vars, context) => {
        toast.error(error.message);
        console.error(error);
        queryClient.setQueryData(transactionsUrl, context.previousTransactions);
      },
      onSettled: async () => {
        await queryClient.invalidateQueries(transactionsUrl);
      },
    }
  );

  const grantBonusMoveMutation = useMutation(
    (transactionId) => {
      const url = `${apiUrl}/leagues/transaction/${transactionId}/bonus`;
      const config = Util.addTokenToHeaders();
      console.log(`sending PATCH request to url ${url} with no data`);
      return http.patch(url, null, config);
    },
    {
      onSuccess: () => {
        console.log("grant bonus move mutation - success");
      },
      onError: (error) => {
        toast.error(error.message);
      },
      onSettled: async () => {
        await queryClient.invalidateQueries(transactionsUrl);
      },
    }
  );

  const isLoading = useIsLoading(teams, teamsQuery, playersData, playersQuery);
  if (isLoading) return <Loading />;

  if (transactions.length === 0) return <p>No transactions found.</p>;

  const hasUndos = transactions.some((transaction) => !!transaction.undone);

  let hasBonus = false;

  return (
    <div>
      <StyledTable>
        <thead>
          <tr>
            <th>Week</th>
            <th>Date</th>
            <th>User</th>
            <th>Team</th>
            <th>In</th>
            <th>Out</th>
            {hasUndos ? <th>Undone</th> : null}
            {adminMode ? (
              <>
                <th>Admin</th>
                <th>Bonus</th>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {[...transactions].reverse().map((transaction) => {
            const {
              id,
              date,
              team: teamId,
              assetsIn,
              assetsOut,
              user: username,
              week,
              undone,
              isBonus,
            } = transaction;

            if (isBonus) hasBonus = true;

            const team = teams.getTeamById(teamId);
            const teamLink = `/${leagueUrl}/${seasonUrl}/${team.idOrSlug}/${weekUrl}`;

            const printPlayers = (assets, isOut = false) => {
              return (
                <div>
                  {assets.map(({ assetType, asset: assetId }) => {
                    if (assetType === "Player") {
                      const playerId = assetId;
                      const player = playersData.players[playerId];
                      return (
                        <div
                          key={playerId}
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <PlayerImageByPos
                            player={player}
                            style={{
                              maxWidth: "18px",
                            }}
                          />
                          <div style={{ marginLeft: "0.25rem" }}>
                            {isBonus && isOut ? (
                              <span style={{ color: "red" }}>*</span>
                            ) : null}
                            {getPlayerName(player, {
                              pos: 1,
                              nfl: 1,
                              injury: 1,
                            })}
                          </div>
                        </div>
                      );
                    } else {
                      return null;
                    }
                  })}
                </div>
              );
            };

            let playerIn = null;
            if (assetsIn) playerIn = printPlayers(assetsIn);

            let playerOut = null;
            if (assetsOut) playerOut = printPlayers(assetsOut, true);

            let undoneClass = undone ? "undone" : null;

            return (
              <tr key={id} className={undoneClass}>
                <td>{week}</td>
                <td>{moment(date).format("ddd MMM D [at] h:mm a")}</td>
                <td>{username}</td>
                <td>
                  <TeamLogo
                    team={team}
                    linkPath={teamLink}
                    size={"sm"}
                    week={week}
                  />
                </td>
                <td>{playerIn}</td>
                <td>{playerOut}</td>
                {hasUndos ? undone ? <td>Undone</td> : <td>&nbsp;</td> : null}
                {adminMode ? (
                  !undone ? (
                    <>
                      <td>
                        <button
                          className={"btn"}
                          onClick={() => undoTransactionMutation.mutate(id)}
                        >
                          Undo
                        </button>
                      </td>
                      <td>
                        <button
                          className={"btn"}
                          onClick={() => grantBonusMoveMutation.mutate(id)}
                        >
                          Bonus
                        </button>
                      </td>
                    </>
                  ) : (
                    <td>&nbsp;</td>
                  )
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </StyledTable>
      {hasBonus ? (
        <div
          style={{
            width: "100%",
            backgroundColor: "white",
            color: "black",
            padding: "0.5rem 1rem",
          }}
        >
          <span style={{ color: "red" }}>*</span> - Bonus Move Granted
        </div>
      ) : null}
    </div>
  );
}
